// Avisa quando o app publicado ficou diferente do que está rodando nesta aba.
//
// POR QUE PRECISA EXISTIR. O app é uma SPA: depois de carregada, navegar por dentro
// dela NUNCA rebusca o `index.html`. Quem deixa a aba aberta continua rodando o
// JavaScript de quando abriu — por horas ou dias — e não tem como saber disso.
//
// Em 29/09/2026 isso custou três diagnósticos errados no mesmo dia: módulo publicado
// que "não aparecia", aba de usuários que "não existia" e tela de login sem as opções
// novas. Nos três casos o servidor estava certo e a aba era velha. O sintoma é sempre
// o mesmo e engana bem: a pessoa jura que a funcionalidade não está lá.
//
// COMO DETECTA. Busca o `/index.html` e compara o nome do script de entrada com o que
// esta página carregou. O nome traz hash de conteúdo, então mudou o nome = mudou o
// código. Não precisa de arquivo de versão nem de endpoint próprio — e o nginx já
// serve o index com `no-store`, então a busca sempre bate no servidor.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';

/** Nome do script de entrada que ESTA página carregou. */
function entradaAtual(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return s?.getAttribute('src') ?? null;
}

export default function AvisoNovaVersao() {
  const [novaVersao, setNovaVersao] = useState(false);
  const atual = useRef<string | null>(null);
  const bg = useColorModeValue('green.500', 'green.400');

  const conferir = useCallback(async () => {
    if (!atual.current) return;
    try {
      // `no-store` nos dois lados: sem isto o próprio fetch poderia ser servido do
      // cache e a comparação seria sempre "igual".
      const r = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) return;
      const html = await r.text();
      const m = html.match(/<script[^>]+type="module"[^>]+src="([^"]+)"/);
      if (m && m[1] && m[1] !== atual.current) setNovaVersao(true);
    } catch { /* offline: não é motivo para incomodar ninguém */ }
  }, []);

  useEffect(() => {
    atual.current = entradaAtual();
    if (!atual.current) return;          // dev server: não há script com hash

    // Ao voltar para a aba é o momento de maior valor: a pessoa saiu, voltou, e é
    // quando ela vai tentar usar algo que talvez tenha acabado de mudar.
    const aoFocar = () => { void conferir(); };
    window.addEventListener('focus', aoFocar);
    const t = setInterval(() => { void conferir(); }, 5 * 60 * 1000);
    void conferir();
    return () => { window.removeEventListener('focus', aoFocar); clearInterval(t); };
  }, [conferir]);

  if (!novaVersao) return null;

  return (
    <Box position="fixed" bottom={4} left="50%" transform="translateX(-50%)" zIndex={2000}
      bg={bg} color="white" px={4} py={2.5} borderRadius="full" boxShadow="lg">
      <HStack spacing={3}>
        <Text fontSize="sm" fontWeight="medium">Nova versão publicada</Text>
        {/* `reload()` e não `location.href`: precisa rebuscar o index, não navegar. */}
        <Button size="xs" colorScheme="whiteAlpha" leftIcon={<RefreshCw size={13} />}
          onClick={() => window.location.reload()}>
          Recarregar
        </Button>
      </HStack>
    </Box>
  );
}
